import { type Plugin } from "vite";
import { resolve, join } from "path";
import fs from "fs";

export interface QuantumForgePluginOptions {
  /**
   * Directory containing the WASM build artifacts.
   * Default: "dist" (relative to project root)
   */
  wasmDir?: string;

  /**
   * URL path prefix where WASM files are served during dev.
   * Default: "/quantum-forge"
   */
  servePath?: string;
}

/**
 * Vite plugin that serves Quantum Forge WASM artifacts during development.
 *
 * During dev, intercepts requests to `servePath/*` and serves matching
 * `quantum-forge-web-*` files from `wasmDir/`. Also excludes the WASM
 * module from Vite's dependency optimization.
 *
 * Usage:
 * ```ts
 * import { quantumForgeVitePlugin } from "@quantum-native/quantum-forge/vite-plugin";
 * export default defineConfig({
 *   plugins: [quantumForgeVitePlugin()],
 * });
 * ```
 */
export function quantumForgeVitePlugin(
  options: QuantumForgePluginOptions = {},
): Plugin {
  const wasmDir = options.wasmDir ?? "dist";
  const servePath = options.servePath ?? "/quantum-forge";
  const prefix = servePath.endsWith("/") ? servePath : servePath + "/";

  return {
    name: "quantum-forge",

    config() {
      return {
        optimizeDeps: {
          exclude: ["quantum-forge-web-api.mjs"],
        },
      };
    },

    configureServer(server) {
      server.middlewares.use((req, res, next) => {
        if (!req.url) return next();

        // Strip query string for matching
        const urlPath = req.url.split("?")[0];

        // Match requests under the serve path (default and variant builds).
        // Default: /quantum-forge/file → dist/file
        // Variant: /quantum-forge-{name}/file → dist/quantum-forge-{name}/file
        if (!urlPath.startsWith(prefix) && !urlPath.startsWith(servePath + "-"))
          return next();

        let filename: string;
        let subdir: string;

        if (urlPath.startsWith(prefix)) {
          // Default build: /quantum-forge/file
          filename = urlPath.slice(prefix.length);
          subdir = "";
        } else {
          // Variant build: /quantum-forge-{name}/file
          // Extract variant name from path segment after servePath-
          const rest = urlPath.slice(servePath.length + 1); // strip "/quantum-forge-"
          const slashIdx = rest.indexOf("/");
          if (slashIdx === -1) return next();
          const variantName = rest.slice(0, slashIdx);
          filename = rest.slice(slashIdx + 1);
          subdir = `quantum-forge-${variantName}`;
        }

        // Only serve quantum-forge files
        if (!filename.startsWith("quantum-forge-web-")) return next();

        // Check project-local dist/ first, then fall back to the framework's
        // dist/ inside node_modules (consumer installs via registry)
        const localDir = subdir
          ? resolve(process.cwd(), wasmDir, subdir)
          : resolve(process.cwd(), wasmDir);
        const nmPkgNames = ["quantum-forge", "@quantum-native/quantum-forge"];
        const candidates = [resolve(localDir, filename)];
        for (const pkg of nmPkgNames) {
          const nmDir = subdir
            ? resolve(process.cwd(), "node_modules", ...pkg.split("/"), "dist", subdir)
            : resolve(process.cwd(), "node_modules", ...pkg.split("/"), "dist");
          candidates.push(resolve(nmDir, filename));
        }
        const filePath = candidates.find((p) => fs.existsSync(p));
        if (!filePath) return next();

        const ext = filename.split(".").pop();
        const mimeTypes: Record<string, string> = {
          mjs: "application/javascript",
          wasm: "application/wasm",
          mts: "application/javascript",
        };
        res.setHeader(
          "Content-Type",
          mimeTypes[ext || ""] || "application/octet-stream",
        );
        fs.createReadStream(filePath).pipe(res);
      });
    },

    writeBundle(options) {
      const outDir = options.dir ?? resolve(process.cwd(), "dist");

      const wasmFiles = [
        "quantum-forge-web-esm.mjs",
        "quantum-forge-web-esm.wasm",
        "quantum-forge-web-api.mjs",
      ];

      const localBase = resolve(process.cwd(), wasmDir);
      const nmBases = [
        resolve(process.cwd(), "node_modules", "quantum-forge", "dist"),
        resolve(process.cwd(), "node_modules", "@quantum-native", "quantum-forge", "dist"),
      ];
      const allBases = [localBase, ...nmBases];

      // Copy a set of WASM files from a source dir to a dest dir
      const copyBuild = (srcDirs: string[], destDir: string) => {
        const resolved = wasmFiles
          .map((f) => {
            for (const dir of srcDirs) {
              const p = resolve(dir, f);
              if (fs.existsSync(p)) return p;
            }
            return null;
          })
          .filter((p): p is string => p !== null);

        if (resolved.length === 0) return;

        fs.mkdirSync(destDir, { recursive: true });
        for (const src of resolved) {
          const filename = src.split("/").pop()!;
          fs.copyFileSync(src, join(destDir, filename));
        }
      };

      // Copy default build
      copyBuild(allBases, join(outDir, "quantum-forge"));

      // Copy any variant builds (e.g. quantum-forge-qubit/)
      for (const base of allBases) {
        if (!fs.existsSync(base)) continue;
        for (const entry of fs.readdirSync(base, { withFileTypes: true })) {
          if (entry.isDirectory() && entry.name.startsWith("quantum-forge-")) {
            const variantSrc = resolve(base, entry.name);
            copyBuild([variantSrc], join(outDir, entry.name));
          }
        }
      }
    },
  };
}
